import { FC, useMemo, useState } from "react"
import {
  Brand,
  LeadworkflowConfigJson,
  LeadworkflowDetails
} from "../../../../../../types"
import Input from "../../../../form/input"
import BBSwitch from "../../../../form/Switch"
import Tabs from "../../../../tabs"
import LeadworkflowConfig from "./sections/config"
import LeadworkflowUpload from "./sections/upload"
import LeadworkflowSteps from "./sections/steps"
import BrandAutocomplete from "../../../../brand-autocomplete"

const DEFAULT_TXT_MSGS_DISCLAIMER =
  "I agree to receive text messages regarding mortgage rate updates, loan information, and related services. Message and data rates may apply. Reply STOP to unsubscribe."

/** Syncs disclaimers.txt_msgs — preserves custom edits when toggled on. */
const syncTxtMsgsDisclaimer = (
  config: LeadworkflowConfigJson
): LeadworkflowConfigJson => {
  const show = config.show_txt_msgs_disclaimer === true
  const { txt_msgs, ...restDisclaimers } = config.disclaimers ?? {}

  return {
    ...config,
    show_txt_msgs_disclaimer: show,
    disclaimers: show
      ? {
          ...restDisclaimers,
          txt_msgs: txt_msgs || DEFAULT_TXT_MSGS_DISCLAIMER
        }
      : restDisclaimers
  }
}

interface Props {
  values: LeadworkflowDetails
  brands: Brand[]
  setFormField: (name: string, value: any) => void
}

const ManageLeadworkflowFormContent: FC<Props> = ({
  values,
  brands,
  setFormField
}) => {
  const [tab, setTab] = useState<string>("config") // "config" | "steps" | "upload"

  const selectedBrandId = useMemo(
    () => brands.find(b => b.id === values.brand_id)?.id,
    [brands, values.brand_id]
  )

  const setConfigField = (field: string, value: any) => {
    const next = { ...values.config_json, [field]: value }
    setFormField("config_json", syncTxtMsgsDisclaimer(next))
  }

  const setBgImage = (val: string) => {
    let url = val

    if (process.env.CDN_HOST) {
      url = process.env.CDN_HOST + new URL(url).pathname
    }

    setConfigField("background_img", url)
  }

  const setFullConfigField = (config: any) => {
    setFormField("config_json", syncTxtMsgsDisclaimer(config))
  }

  return (
    <div className="py-6 h-full flex flex-col">
      <div className="grid grid-cols-2 gap-x-12">
        <div>
          <Input label="Name of Lead worflow" name="name" />
        </div>
        <div className="pt-10">
          <BBSwitch
            label="Set as default"
            enabled={values.is_default === "1"}
            setEnabled={value => setFormField("is_default", value ? "1" : "0")}
          />
        </div>
      </div>
      <div className="py-6">
        <BrandAutocomplete
          brands={brands}
          selected={selectedBrandId}
          onChange={val => setFormField("brand_id", val)}
          direction="vertical"
        />
      </div>
      <div className="pt-6">
        <Tabs
          className="px-6"
          currentTab={tab}
          tabs={[
            { id: "config", label: "Config" },
            { id: "steps", label: "Steps" },
            { id: "upload", label: "Upload" }
          ]}
          onChange={setTab}
        />
      </div>
      <div className="pt-6 flex-1">
        {tab === "config" && (
          <LeadworkflowConfig
            values={values}
            setFullConfigField={setFullConfigField}
          />
        )}
        {tab === "steps" && (
          <LeadworkflowSteps values={values} setConfigField={setConfigField} />
        )}
        {tab === "upload" && (
          <LeadworkflowUpload values={values} setBgImage={setBgImage} />
        )}
      </div>
    </div>
  )
}

export default ManageLeadworkflowFormContent
